/**
 * "The client answered this themselves" - the one thing the status pill cannot
 * say.
 *
 * Marking a quote accepted in this screen writes `status` and `acceptedAt`. A
 * client accepting it in the portal writes the same two columns. So on the
 * detail page the two outcomes are pixel-identical, and the question an operator
 * actually asks a week later - did they say yes, or did somebody here type it in
 * after a phone call - has no answer on screen at all.
 *
 * `portalDecision` is the answer, and this card is where it is read - on quotes
 * and on proposals alike. It appears only when the CLIENT wrote it, so its
 * presence IS the fact; an offer settled at the desk shows nothing here rather
 * than an empty panel. `via` says which door the answer came through: the
 * signed-in portal, or the public link printed on the document.
 *
 * The refusal reason is printed in full and never truncated. It is the half of a
 * "no" that can be acted on, and the operator is about to write the next quote.
 */
import { Check, X } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { EstimatePortalDecision } from "../../../lib/sales";
import { Card, fmtDateTime } from "../../ui";

export function PortalDecisionCard({ decision }: { decision: EstimatePortalDecision }) {
  const t = useT();
  const accepted = decision.decision === "accepted";
  const who = decision.name?.trim() || decision.email?.trim() || t("sales.portalDecision.someone");

  return (
    <Card className="mt-4 px-[26px] py-[22px]">
      <div className="crm-card-t mb-2.5 flex items-center gap-2">
        {accepted ? <Check className="size-4 text-emerald-600" /> : <X className="size-4 text-red-600" />}
        {t(accepted ? "sales.portalDecision.accepted" : "sales.portalDecision.declined", { who })}
      </div>
      <p className="crm-mut text-[13px] leading-relaxed">
        {t("sales.portalDecision.when", { when: fmtDateTime(decision.at) })}
        {decision.email ? ` - ${decision.email}` : ""}
        {/* Which door they came through. A portal answer carries an account
            behind the name; a link answer carries only what was typed, and an
            operator reading this later has to be able to tell them apart. */}
        {` - ${t(decision.via === "link" ? "sales.portalDecision.viaLink" : "sales.portalDecision.viaPortal")}`}
      </p>
      {decision.note ? (
        <p className="mt-3 whitespace-pre-wrap text-[13.25px] leading-relaxed">{decision.note}</p>
      ) : null}
    </Card>
  );
}
